/**
 * Defines versioned contracts for platform-level company administration.
 */
import { z } from "zod";
import { PlanCodeSchema } from "./PlanContract.js";
/**
 * Path segments the portal owns; a company slug may never shadow them.
 */
export const ReservedPublicSlugs = Object.freeze([
    "api",
    "libro",
    "seguimiento",
    "_next",
    "health",
    "static",
]);
/**
 * Normalizes and validates the public slug used in portal URLs and subdomains.
 */
export const PublicSlugSchema = z
    .string()
    .trim()
    .toLowerCase()
    .min(2)
    .max(120)
    .regex(/^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/u, "Expected lowercase letters, digits and hyphens.")
    .refine((value) => !ReservedPublicSlugs.includes(value), "The slug is reserved by the portal.");
/**
 * Accepts #RRGGBB in any case and normalizes it to uppercase.
 */
export const BrandColorSchema = z
    .string()
    .trim()
    .transform((value) => (value.startsWith("#") ? value.toUpperCase() : `#${value.toUpperCase()}`))
    .refine((value) => /^#[0-9A-F]{6}$/u.test(value), "Expected a #RRGGBB color.");
export const CompanyStatusSchema = z.enum(["DRAFT", "ACTIVE", "SUSPENDED"]);
export const BranchTypeSchema = z.enum(["PHYSICAL", "VIRTUAL", "OTHER"]);
export const DomainKindSchema = z.enum(["SUBDOMAIN", "CUSTOM"]);
const taxIdSchema = z
    .string()
    .trim()
    .regex(/^\d{11}$/u, "Expected an 11-digit RUC.");
const phoneSchema = z
    .string()
    .trim()
    .min(6)
    .max(32)
    .regex(/^\+?[0-9 ()-]+$/u, "Expected a valid phone number.");
/**
 * Company branding used by the public portal.
 */
export const CompanyBrandingSchema = z
    .object({
    deepColor: BrandColorSchema,
    faviconUrl: z.url().max(2_000).nullable(),
    logoUrl: z.url().max(2_000).nullable(),
    onPrimaryColor: BrandColorSchema,
    primaryColor: BrandColorSchema,
})
    .strict();
export const UpdateCompanyBrandingRequestSchema = CompanyBrandingSchema;
/**
 * A candidate logo URL an operator is typing but has not saved yet.
 */
export const PreviewCompanyBrandingLogoRequestSchema = z
    .object({ url: z.url().max(2_000) })
    .strict();
/**
 * The candidate logo re-encoded as a same-origin data URL, so the backoffice
 * preview never loads an operator-supplied host directly in the browser.
 */
export const PreviewCompanyBrandingLogoResultSchema = z.object({ dataUrl: z.string() }).strict();
/**
 * A hostname that resolves to one company book.
 */
export const CompanyDomainSchema = z
    .object({
    createdAt: z.iso.datetime({ offset: true }),
    hostname: z.string().trim().toLowerCase().min(4).max(253),
    id: z.uuid(),
    isActive: z.boolean(),
    isPrimary: z.boolean(),
    kind: DomainKindSchema,
    verificationToken: z.string().nullable(),
    verifiedAt: z.iso.datetime({ offset: true }).nullable(),
})
    .strict();
export const CreateCompanyDomainRequestSchema = z
    .object({
    hostname: z
        .string()
        .trim()
        .toLowerCase()
        .min(4)
        .max(253)
        .regex(/^(?!-)[a-z0-9-]{1,63}(?<!-)(?:\.(?!-)[a-z0-9-]{1,63}(?<!-))+$/u, "Expected a valid hostname."),
    isPrimary: z.boolean().default(false),
    kind: DomainKindSchema,
})
    .strict();
export const CompanyBranchSchema = z
    .object({
    address: z.string().trim().max(500).nullable(),
    bookCode: z.string().trim().min(1).max(80),
    code: z.string().trim().min(1).max(40),
    department: z.string().trim().max(100).nullable(),
    district: z.string().trim().max(100).nullable(),
    id: z.uuid(),
    isActive: z.boolean(),
    name: z.string().trim().min(1).max(200),
    notificationEmail: z.email().max(254).nullable(),
    province: z.string().trim().max(100).nullable(),
    slug: PublicSlugSchema,
    type: BranchTypeSchema,
})
    .strict();
export const CreateCompanyBranchRequestSchema = z
    .object({
    address: z.string().trim().max(500).nullable().optional(),
    bookCode: z.string().trim().min(1).max(80),
    code: z.string().trim().min(1).max(40),
    department: z.string().trim().max(100).nullable().optional(),
    district: z.string().trim().max(100).nullable().optional(),
    name: z.string().trim().min(1).max(200),
    notificationEmail: z.email().max(254).nullable().optional(),
    province: z.string().trim().max(100).nullable().optional(),
    slug: PublicSlugSchema,
    type: BranchTypeSchema.default("PHYSICAL"),
})
    .strict();
export const UpdateCompanyBranchRequestSchema = CreateCompanyBranchRequestSchema.partial()
    .extend({ isActive: z.boolean().optional() })
    .strict();
const companyCoreShape = {
    consumerSupportChannel: z.string().trim().min(1).max(254),
    contactName: z.string().trim().min(1).max(200),
    dataControllerContact: z.string().trim().min(1).max(254),
    dataControllerName: z.string().trim().min(1).max(200),
    industry: z.string().trim().max(160).nullable().optional(),
    legalAddress: z.string().trim().min(1).max(1_000),
    legalName: z.string().trim().min(1).max(200),
    primaryEmail: z.email().max(254),
    primaryPhone: phoneSchema,
    privacyPolicyText: z.string().trim().max(20_000).nullable().optional(),
    privacyPolicyUrl: z.url().max(2_000).nullable().optional(),
    tradeName: z.string().trim().max(200).nullable().optional(),
};
export const CompanySummarySchema = z
    .object({
    activeBranchCount: z.number().int().nonnegative(),
    createdAt: z.iso.datetime({ offset: true }),
    id: z.uuid(),
    legalName: z.string(),
    planCode: PlanCodeSchema,
    planName: z.string(),
    primaryHostname: z.string().nullable(),
    slug: PublicSlugSchema,
    status: CompanyStatusSchema,
    taxId: taxIdSchema,
    tradeName: z.string().nullable(),
})
    .strict();
export const CompanyListResponseSchema = z
    .object({
    items: z.array(CompanySummarySchema).max(500),
})
    .strict();
export const CompanyDetailSchema = z
    .object({
    administratorEmail: z.string().nullable(),
    administratorFullName: z.string().nullable(),
    branches: z.array(CompanyBranchSchema).max(500),
    branding: CompanyBrandingSchema.nullable(),
    consumerSupportChannel: z.string(),
    contactName: z.string(),
    createdAt: z.iso.datetime({ offset: true }),
    dataControllerContact: z.string(),
    dataControllerName: z.string(),
    allowsCustomDomain: z.boolean(),
    domains: z.array(CompanyDomainSchema).max(100),
    id: z.uuid(),
    industry: z.string().nullable(),
    legalAddress: z.string(),
    legalName: z.string(),
    locale: z.string(),
    /** `companies.platform_org_id`: the Continnua core organization this company is linked to. */
    platformOrgId: z.uuid().nullable(),
    planCode: PlanCodeSchema,
    planName: z.string(),
    primaryEmail: z.string(),
    primaryPhone: z.string(),
    privacyPolicyText: z.string().nullable(),
    privacyPolicyUrl: z.string().nullable(),
    slug: PublicSlugSchema,
    status: CompanyStatusSchema,
    taxId: taxIdSchema,
    timezone: z.string(),
    tradeName: z.string().nullable(),
})
    .strict();
/**
 * Single transactional request that leaves a company ready for a demo.
 */
export const CreateCompanyRequestSchema = z
    .object({
    ...companyCoreShape,
    administrator: z
        .object({
        email: z.email().max(254),
        fullName: z.string().trim().min(1).max(200),
        password: z.string().min(12).max(128),
    })
        .strict(),
    branch: CreateCompanyBranchRequestSchema,
    branding: CompanyBrandingSchema.optional(),
    /**
     * Links this company to an already-existing Continnua core organization (created from the
     * platform console) at creation time, instead of leaving it to the legacy
     * `platform:migrate` CLI. Omit to create an unlinked company, as before.
     */
    platformOrgId: z.uuid().optional(),
    planCode: PlanCodeSchema.default("INICIO"),
    slug: PublicSlugSchema,
    taxId: taxIdSchema,
})
    .strict();
export const UpdateCompanyRequestSchema = z
    .object({
    ...companyCoreShape,
    /**
     * Links (or, passed as `null`, unlinks) `companies.platform_org_id`. Omit the field entirely
     * to leave the current link untouched -- this is a full-replace schema for every other field,
     * but this one is change-on-provide so an operator updating unrelated company details never
     * accidentally clears an existing link.
     */
    platformOrgId: z.uuid().nullable().optional(),
    status: CompanyStatusSchema,
})
    .strict();
/**
 * Values a company created automatically from the Continnua core carries until
 * its administrator completes them: the core does not model these fields, but
 * the columns are NOT NULL.
 */
export const CompanyPlaceholderValues = Object.freeze({
    email: "pendiente@placeholder.invalid",
    phone: "000000",
    text: "Pendiente de completar",
});
/**
 * Whether a stored company field still holds a provisioning placeholder.
 */
export function IsCompanyPlaceholder(value) {
    if (value === null || value === undefined || value.trim().length === 0) {
        return true;
    }
    return (value === CompanyPlaceholderValues.text ||
        value === CompanyPlaceholderValues.email ||
        value === CompanyPlaceholderValues.phone);
}
/**
 * Parts of the company profile its administrator still has to define. The
 * public book stays closed while `PRIVACY_POLICY` is pending.
 */
export const CompanyProfilePendingItemSchema = z.enum([
    "BRANDING",
    "CONSUMER_SUPPORT_CHANNEL",
    "CONTACT",
    "DATA_CONTROLLER",
    "LEGAL_ADDRESS",
    "PRIVACY_POLICY",
]);
/**
 * The active company's own profile, as its administrator sees and edits it.
 */
export const CompanyProfileSchema = z
    .object({
    branding: CompanyBrandingSchema.nullable(),
    consumerSupportChannel: z.string(),
    contactName: z.string(),
    dataControllerContact: z.string(),
    dataControllerName: z.string(),
    industry: z.string().nullable(),
    legalAddress: z.string(),
    legalName: z.string(),
    pending: z.array(CompanyProfilePendingItemSchema).max(10),
    primaryEmail: z.string(),
    primaryPhone: z.string(),
    privacyPolicyText: z.string().nullable(),
    privacyPolicyUrl: z.string().nullable(),
    slug: PublicSlugSchema,
    taxId: taxIdSchema,
    tradeName: z.string().nullable(),
})
    .strict();
/**
 * What a company administrator may change. RUC, slug and legal name stay with
 * the platform (the legal name follows the Continnua core), and the privacy
 * notice is required because the public book cannot open without it.
 */
export const UpdateCompanyProfileRequestSchema = z
    .object({
    consumerSupportChannel: companyCoreShape.consumerSupportChannel,
    contactName: companyCoreShape.contactName,
    dataControllerContact: companyCoreShape.dataControllerContact,
    dataControllerName: companyCoreShape.dataControllerName,
    industry: companyCoreShape.industry,
    legalAddress: companyCoreShape.legalAddress,
    primaryEmail: companyCoreShape.primaryEmail,
    primaryPhone: companyCoreShape.primaryPhone,
    privacyPolicyText: z.string().trim().min(1).max(20_000),
    privacyPolicyUrl: companyCoreShape.privacyPolicyUrl,
    tradeName: companyCoreShape.tradeName,
})
    .strict();
/**
 * Returned right after provisioning so the operator can open the demo book.
 */
export const CompanyProvisioningResultSchema = z
    .object({
    administratorEmail: z.string(),
    branchSlug: PublicSlugSchema,
    company: CompanyDetailSchema,
    publicBookPath: z.string(),
})
    .strict();
/**
 * Public host resolution consumed by the portal middleware.
 */
export const HostResolutionResponseSchema = z
    .object({
    branchSlug: PublicSlugSchema.nullable(),
    companySlug: PublicSlugSchema,
})
    .strict();
export const DomainVerificationResultSchema = z
    .object({
    domain: CompanyDomainSchema,
    expectedRecord: z.string(),
    verified: z.boolean(),
})
    .strict();
/**
 * Platform operator sets a new password for the active company administrator.
 */
export const ResetCompanyAdministratorCredentialsRequestSchema = z
    .object({
    password: z.string().min(12).max(128),
    reason: z.string().trim().min(8).max(1_000).optional(),
})
    .strict();
export const ResetCompanyAdministratorCredentialsResultSchema = z
    .object({
    administratorEmail: z.email().max(254),
})
    .strict();
/**
 * Shared payload for welcome and credential-reset emails to the company administrator.
 */
export const CompanyAdministratorCredentialsEventPayloadSchema = z
    .object({
    administratorEmail: z.email().max(254),
    administratorUserId: z.uuid(),
    companyName: z.string().min(1).max(200),
    fullName: z.string().min(1).max(200),
    initialPassword: z.string().min(12).max(128),
    loginUrl: z.url().max(2_000),
    occurredAt: z.iso.datetime({ offset: true }),
})
    .strict();
/**
 * Versioned outbox event emitted after a company and its first administrator exist.
 */
export const CompanyAdministratorProvisionedEventName = "companies.administrator-provisioned.v1";
/**
 * Versioned outbox event emitted when a platform operator resets administrator credentials.
 */
export const CompanyAdministratorCredentialsResetEventName = "companies.administrator-credentials-reset.v1";
export const CompanyAdministratorProvisionedEventPayloadSchema = CompanyAdministratorCredentialsEventPayloadSchema;
//# sourceMappingURL=PlatformContract.js.map